import React, { useContext, useEffect } from 'react'
import PropTypes from 'prop-types'

import useSetState from 'hooks/useSetState'
import findDataStoreItemValue from 'functions/findDataStoreItemValue'

import GlobalContext from 'agency/digital_renderer/utils/context'
import deviceStyle from 'agency/digital_renderer/utils/deviceStyle'

import styles from './styles.scss'

const updateDataStore = (value, props) => {
  const {
    callbacks: { updateDataStore: updateDataStoreFn },
    options,
  } = props

  const {
    inputDataHidden,
    inputDataStoreBehaviour,
    inputDataStoreKey,
    inputDataStoreLabel,
    inputDataStoreTags,
    inputRequired,
    inputSecured,
  } = options || {}

  const payload = {
    key: inputDataStoreKey,
    label: inputDataStoreLabel,
    behaviour: inputDataStoreBehaviour,
    value,
    data: {
      hidden: inputDataHidden || false,
      required: inputRequired,
      secured: inputSecured || false,
      tags: inputDataStoreTags,
    },
  }

  updateDataStoreFn(payload)
}

const isEditableByUser = (currentUser, inputEditableBy) => {
  if (inputEditableBy && currentUser){
    if (inputEditableBy === 'Agency'){
      return currentUser.actable_type === 'Agent' || currentUser.actable_type === 'OfficeAdministrator'
    }

    return inputEditableBy === currentUser.actable_type
  }

  return true
}

const changeCheckboxValue = (value, props, setState) => {
  const friendlyValue = value ? 'Yes' : 'No'

  setState({ inputValue: friendlyValue })
  updateDataStore(friendlyValue, props)
}

const defaultState = {
  inputValue: '',
}

const Input = (props) => {
  const { options } = props

  const style = deviceStyle(props)

  const [state, setState] = useSetState(defaultState)
  const { inputValue } = state

  const defaultContext = useContext(GlobalContext)
  const { currentUser, dataStoreItems, uuid } = defaultContext

  const {
    inputComponent,
    inputDataStoreBehaviour,
    inputDataStoreKey,
    inputEditableBy,
    inputOptions,
    inputPlaceholder,
    inputType,
  } = options || {}

  const dataStoreItemValue = findDataStoreItemValue(dataStoreItems, inputDataStoreBehaviour, inputDataStoreKey, {
    editorId: currentUser.id,
    editorType: 'User',
    groupKey: uuid,
  })

  useEffect(() => {
    setState({ inputValue: dataStoreItemValue })
  }, [dataStoreItemValue])

  const isEditable = isEditableByUser(currentUser, inputEditableBy)

  if (!isEditable){
    return <div className={styles.disabled} style={style}>{inputValue}</div>
  }

  return (
    <div className={styles.root}>
      {inputComponent === 'input' && inputType === 'checkbox' && (
        <input
          checked={inputValue === 'Yes'}
          className={styles.input}
          onChange={e => changeCheckboxValue(e.target.checked, props, setState)}
          style={style}
          type="checkbox"
        />
      )}

      {inputComponent === 'input' && inputType !== 'checkbox' && (
        <input
          className={styles.input}
          onBlur={() => updateDataStore(inputValue, props)}
          onChange={e => setState({ inputValue: e.target.value })}
          placeholder={inputPlaceholder}
          style={style}
          type={inputType || 'text'}
          value={inputValue}
        />
      )}

      {inputComponent === 'select' && (
        <select
          className={styles.input}
          value={inputValue}
          onChange={e => updateDataStore(e.target.value, props)}
          style={style}
        >
          <option value="">Please Select...</option>
          {inputOptions
            && inputOptions.map(option => (
              <option key={option.key} value={option.value}>
                {option.value}
              </option>
            ))}
        </select>
      )}

      {inputComponent === 'textarea' && (
        <textarea
          className={styles.input}
          onBlur={() => updateDataStore(inputValue, props, state)}
          onChange={e => setState({ inputValue: e.target.value })}
          placeholder={inputPlaceholder}
          style={style}
          value={inputValue}
        />
      )}
    </div>
  )
}

Input.propTypes = {
  callbacks: PropTypes.shape({
    updateDataStore: PropTypes.func.isRequired,
  }),
  options: PropTypes.object,
  style: PropTypes.object,
}

Input.defaultProps = {
  options: {},
}

export default Input
